import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { FileText, Home, CreditCard, IdCard, Wallet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface ChecklistItem {
  id: string;
  name: string;
  icon: JSX.Element;
  isUploaded: boolean;
}

export const DocumentChecklist = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<string[]>([]);

  const checklistItems: ChecklistItem[] = [
    { id: "id-document", name: "ID Document", icon: <IdCard className="w-5 h-5" />, isUploaded: false },
    { id: "proof-of-residence", name: "Proof of Residence", icon: <Home className="w-5 h-5" />, isUploaded: false },
    { id: "fica", name: "FICA", icon: <CreditCard className="w-5 h-5" />, isUploaded: false },
    { id: "payslip", name: "Payslip", icon: <FileText className="w-5 h-5" />, isUploaded: false },
    { id: "bank-statements", name: "Bank Statements (3 Months)", icon: <Wallet className="w-5 h-5" />, isUploaded: false },
  ];

  useEffect(() => {
    if (user) {
      fetchDocuments();
    }
  }, [user]);

  useEffect(() => {
    // Listen for document upload events
    const handleDocumentUploaded = () => {
      fetchDocuments();
    };

    window.addEventListener('documentUploaded', handleDocumentUploaded);
    return () => {
      window.removeEventListener('documentUploaded', handleDocumentUploaded);
    };
  }, []);

  const fetchDocuments = async () => {
    try {
      const { data } = await supabase
        .from('documents')
        .select('name')
        .eq('user_id', user?.id);
      
      setDocuments(data?.map(doc => doc.name.toLowerCase()) || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const isDocumentUploaded = (itemName: string) => {
    return documents.some(doc => 
      doc.toLowerCase().includes(itemName.toLowerCase())
    );
  };

  return (
    <Card className="glass-card p-6 space-y-4 max-w-md w-full mx-auto">
      <h2 className="text-xl font-semibold text-foreground">Required Documents</h2>
      
      <div className="text-sm text-muted-foreground mb-4 bg-secondary/30 p-3 rounded-lg">
        We require these documents to ensure the security and compliance of our services. 
        All uploaded files are encrypted and stored securely in our private system, 
        accessible only to authorized personnel. Your privacy and data security are our 
        top priorities.
      </div>

      <div className="space-y-3">
        {checklistItems.map((item) => (
          <div
            key={item.id}
            className="flex items-center space-x-3 p-2 rounded-lg bg-secondary/50"
          >
            {item.icon}
            <span className="flex-1 text-foreground">{item.name}</span>
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center ${
                isDocumentUploaded(item.name)
                  ? "bg-green-500"
                  : "bg-secondary border-2 border-muted-foreground"
              }`}
            >
              {isDocumentUploaded(item.name) && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};